import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, BookOpen, Clock, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import apiClient from '../services/apiClient';

/**
 * StudentCourseWorkspacePage - Sprint 1 Week 2
 * 
 * Vista de Workspace del Curso - Capa 2 del Student Execution System
 * Propósito:
 * - Mostrar grupo asignado y miembros
 * - Mostrar semana actual activa  
 * - Mostrar progreso general del grupo
 * - Botón "Trabajar semana" → lleva a ejecución
 * 
 * IMPORTANTE: Esta página se usa CON DashboardLayout (no incluido aquí)
 * Route: /course/:sectionId/workspace
 */

const StudentCourseWorkspacePage = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [courseDefinition, setCourseDefinition] = useState(null);
  const [myGroup, setMyGroup] = useState(null);
  const [courseRun, setCourseRun] = useState(null);
  const [weeklyProgress, setWeeklyProgress] = useState([]);

  useEffect(() => {
    loadWorkspaceData();
  }, [sectionId]);

  const loadWorkspaceData = async () => {
    try {
      setLoading(true);
      
      // 1. Obtener mi grupo para esta sección
      const groupsResponse = await apiClient.get('/student-execution/my-groups');
      const allGroups = groupsResponse.data.data || [];
      
      // Debug: Ver qué grupos se reciben
      console.log('📊 Grupos recibidos:', allGroups);
      console.log('🎯 Buscando sectionId:', sectionId);
      
      // Filtrar grupos por sectionId (arquitectura correcta: estudiantes asignados a SECCIONES)
      const sectionGroups = allGroups.filter(group => {
        // group.sectionId puede ser string o objeto {sectionId: "...", name: "...", ...}
        const groupSectionId = typeof group.sectionId === 'string' 
          ? group.sectionId 
          : group.sectionId?.sectionId;
        const matches = groupSectionId === sectionId;
        console.log(`🔍 Grupo ${group.name}: sectionId="${groupSectionId}", matches=${matches}`);
        return matches;
      });
      
      if (sectionGroups.length === 0) {
        console.error('❌ No se encontraron grupos para esta sección');
        console.error('Grupos disponibles:', allGroups.map(g => ({ 
          name: g.name, 
          sectionId: typeof g.sectionId === 'string' ? g.sectionId : g.sectionId?.sectionId
        })));
        setError(`No estás asignado a ningún grupo en esta sección. SectionId buscado: "${sectionId}"`);
        return;
      }
      
      console.log('✅ Grupo encontrado:', sectionGroups[0]);
      const group = sectionGroups[0]; // Un estudiante solo puede estar en un grupo por sección
      setMyGroup(group);
      
      // 2. Obtener definición del curso (endpoint para estudiantes)
      // Extraer courseId desde la sección o grupo
      const resolvedCourseId = group.courseId || group.courseInfo?.courseId;
      const courseResponse = await apiClient.get(`/student-execution/courses/${resolvedCourseId}`);
      setCourseDefinition(courseResponse.data.course);
      
      // 3. La sección ya viene en group.sectionId (es un objeto, no solo un ID)
      console.log('📋 Sección:', group.sectionId);
      setCourseRun(group.sectionId); // Objeto completo con {name, currentWeek, status, etc.}
      
      // 4. Obtener progreso semanal del grupo
      const progressResponse = await apiClient.get(`/student-execution/groups/${group.groupId}/progress`);
      setWeeklyProgress(progressResponse.data.data?.weeklyProgress || []);
      
    } catch (error) {
      console.error('Error loading workspace data:', error);
      setError('Error al cargar información del workspace');
    } finally {
      setLoading(false);
    }
  };

  const getWeekStatusIcon = (weekProgress) => {
    switch (weekProgress.status) {
      case 'completed':
      case 'declared':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
      case 'ready_for_review':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getWeekStatusText = (weekProgress) => {
    switch (weekProgress?.status) {
      case 'Completed':
      case 'Finalized':
        return 'Completado';
      case 'InProgress':
        return 'En progreso';
      case 'ReadyForReview':
        return 'Listo para revisar';
      default:
        return 'Pendiente';
    }
  };

  const handleWorkWeek = (weekNumber) => {
    navigate(`/course/${sectionId}/week/${weekNumber}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <h3 className="text-red-800 font-medium">Error</h3>
        </div>
        <p className="text-red-700 mt-2">{error}</p>
      </div>
    );
  }

  if (!myGroup || !courseDefinition || !courseRun) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-gray-900 font-medium">No se pudo cargar el workspace</h3>
        <p className="text-gray-600 mt-1">Información incompleta del curso o grupo</p>
        <pre className="mt-4 text-xs text-left bg-gray-100 p-4 rounded overflow-auto">
          {JSON.stringify({ hasGroup: !!myGroup, hasCourse: !!courseDefinition, hasSection: !!courseRun }, null, 2)}
        </pre>
      </div>
    );
  }

  const currentWeek = courseRun?.currentWeek || 1;
  const currentWeekProgress = weeklyProgress.find(wp => wp.weekNumber === currentWeek);
  const totalWeeks = courseDefinition.weeks ? courseDefinition.weeks.length : 0;
  const completedWeeks = weeklyProgress.filter(wp => wp.status === 'Finalized' || wp.status === 'Completed').length;
  const progressPercentage = totalWeeks > 0 ? Math.round((completedWeeks / totalWeeks) * 100) : 0;

  return (
    <div className="space-y-5 p-6 md:p-8">

      {/* ===== COURSE WORKSPACE HEADER ===== */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        {/* Breadcrumb: Programas / {curso} / Progreso */}
        <nav className="flex items-center gap-1.5 text-sm mb-3">
          <button
            onClick={() => navigate('/cursos-grupales')}
            className="text-gray-500 hover:text-gray-800 transition-colors"
          >
            Programas
          </button>
          <span className="text-gray-300">/</span>
          <button
            onClick={() => navigate(`/group-courses/${courseId}`)}
            className="text-gray-500 hover:text-gray-800 transition-colors"
          >
            {courseDefinition.title?.replace(/^Programa\s+/i, '')}
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-gray-900 font-medium">Progreso</span>
        </nav>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 mb-0.5">
              {courseDefinition.title}
            </h1>
            <p className="text-xs text-gray-400 mb-1">EduTech Pulse Lab</p>
            <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap">
              <span className="flex items-center gap-1">
                <BookOpen className="w-4 h-4" />
                {totalWeeks} semanas
              </span>
              <span className="text-gray-300">•</span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                Curso grupal
              </span>
            </div>
          </div>
          <div className="flex-shrink-0">
            {/* Mobile: solo porcentaje */}
            <div className="sm:hidden text-right">
              <div className="text-2xl font-bold text-blue-600">{progressPercentage}%</div>
              <div className="text-xs text-gray-500">Progreso general</div>
            </div>
            {/* Desktop: ladrillos */}
            <div className="hidden sm:block">
              <div className="flex gap-0.5 mb-1">
                {Array.from({ length: totalWeeks || 18 }).map((_, index) => {
                  const weekNumber = index + 1;
                  const progress = weeklyProgress.find(wp => wp.weekNumber === weekNumber);
                  const isCompleted = progress?.status === 'Completed' || progress?.status === 'Finalized';
                  const isActive = weekNumber === currentWeek;
                  return (
                    <div
                      key={weekNumber}
                      title={`Semana ${weekNumber}`}
                      className={`w-4 h-3 rounded-sm ${
                        isCompleted ? 'bg-cyan-400' : isActive ? 'bg-blue-500' : 'bg-gray-200'
                      }`}
                    />
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 text-right">
                {completedWeeks} de {totalWeeks} sem. · {progressPercentage}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== ACTIVE WEEK HERO ===== */}
      {currentWeek && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl py-7 px-8 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-blue-200 text-xs font-semibold uppercase tracking-wide mb-1">
                Semana activa
              </p>
              <h2 className="text-xl font-bold mb-1">
                Semana {currentWeek} —{' '}
                {courseDefinition.weeks?.[currentWeek - 1]?.title || `Semana ${currentWeek}`}
              </h2>
              {courseDefinition.weeks?.[currentWeek - 1]?.learningObjectives?.length > 0 && (
                <div className="mt-2 mb-2 space-y-0.5">
                  {courseDefinition.weeks[currentWeek - 1].learningObjectives.map((obj, idx) => (
                    <p key={idx} className="text-blue-100 text-xs">
                      Objetivo {idx + 1}: {obj}
                    </p>
                  ))}
                </div>
              )}
              {currentWeekProgress && (
                <p className="text-blue-200 text-sm flex items-center gap-1.5">
                  {getWeekStatusIcon(currentWeekProgress)}
                  {getWeekStatusText(currentWeekProgress)}
                </p>
              )}
            </div>
            <button
              onClick={() => handleWorkWeek(currentWeek)}
              className="flex-shrink-0 bg-white text-blue-600 px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2 text-sm"
            >
              Continuar semana
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ===== WEEKS PROGRESS GRID ===== */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="font-semibold text-gray-900 mb-4 text-sm flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-600" />
          Progreso del curso
          <span className="text-gray-400 font-normal ml-1">
            {completedWeeks} de {totalWeeks} semanas completadas
          </span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          {courseDefinition.weeks?.map((week, index) => {
            const weekNumber = index + 1;
            const progress = weeklyProgress.find(wp => wp.weekNumber === weekNumber);
            const isCurrentWeek = weekNumber === currentWeek;
            const isCompleted = progress?.status === 'Completed' || progress?.status === 'Finalized';
            const isAccessible = weekNumber <= currentWeek;

            return (
              <button
                key={weekNumber}
                onClick={() => isAccessible && handleWorkWeek(weekNumber)}
                disabled={!isAccessible}
                className={`p-2.5 rounded-lg border text-left transition-all ${
                  isCurrentWeek
                    ? 'border-blue-400 bg-blue-50 cursor-pointer'
                    : isCompleted
                    ? 'border-cyan-300 bg-cyan-50 cursor-pointer'
                    : isAccessible
                    ? 'border-gray-300 bg-white hover:border-gray-400 cursor-pointer'
                    : 'border-gray-100 bg-gray-50 cursor-default opacity-40'
                }`}
              >
                <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${
                  isCurrentWeek ? 'text-blue-600' : isCompleted ? 'text-cyan-600' : isAccessible ? 'text-gray-500' : 'text-gray-300'
                }`}>
                  Sem. {weekNumber}
                </p>
                <p className={`text-xs leading-tight line-clamp-2 ${
                  isCurrentWeek ? 'text-blue-800' : isCompleted ? 'text-cyan-800' : 'text-gray-500'
                }`}>{week.title}</p>
                {(isCurrentWeek || isCompleted) && (
                  <p className={`text-xs mt-1.5 font-medium ${
                    isCompleted ? 'text-cyan-500' : 'text-blue-500'
                  }`}>
                    {isCompleted ? 'Completado' : 'En progreso'}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ===== GROUP SECTION ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Mi Grupo */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4 text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            Mi Grupo:{' '}
            <span className="text-blue-700">{myGroup.groupName || myGroup.name || 'Sin nombre'}</span>
          </h2>
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">Integrantes</p>
          <div className="space-y-2">
            {myGroup.members.filter(member => member.status === 'active').map((member, index) => (
              <div key={member.userId} className="flex items-center gap-2.5">
                <div className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {member.userName ? member.userName.charAt(0).toUpperCase() : `M${index + 1}`}
                </div>
                <span className="text-sm text-gray-800">{member.userName || `Miembro ${index + 1}`}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Información de la sección */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4 text-sm flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-blue-600" />
            Información de la sección
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-1.5 border-b border-gray-50">
              <span className="text-gray-500">Sección</span>
              <span className="font-medium text-gray-800">{courseRun?.name || 'Sin sección'}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-gray-50">
              <span className="text-gray-500">Semana actual</span>
              <span className="font-medium text-gray-800">{currentWeek} de {totalWeeks}</span>
            </div>
            <div className="flex justify-between py-1.5">
              <span className="text-gray-500">Estado</span>
              <span className={`font-medium capitalize ${
                courseRun?.status === 'active' ? 'text-green-600' : 'text-gray-800'
              }`}>
                {courseRun?.status || 'N/A'}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StudentCourseWorkspacePage;